/**
 * 開発者設定関連のモックサービス
 * 本番環境では AWS API に置き換える予定
 *
 * 機能:
 * - 施設情報の取得・更新
 * - 座標変換補正値の取得・更新
 * - ネットワーク遅延のシミュレート
 * - エラーパターンのシミュレート
 */

// モックデータ - 施設情報
let mockFacilities = [
  {
    id: 1,
    name: '市役所',
    latitude: 35.975411,
    longitude: 139.757172,
    updatedAt: new Date().toISOString()
  },
  {
    id: 2,
    name: 'クリーンセンター①',
    latitude: null,
    longitude: null,
    updatedAt: new Date().toISOString()
  },
  {
    id: 3,
    name: 'クリーンセンター②',
    latitude: null,
    longitude: null,
    updatedAt: new Date().toISOString()
  }
]

// モックデータ - 座標変換補正値
let mockCoordinateCorrection = {
  latitudeOffset: 0.000000,
  longitudeOffset: -0.000001,
  updatedAt: new Date().toISOString()
}

// モックデータ - 企業リスト
const mockCompanyLists = {
  // 開発者は PITS 固定
  開発者: ['PITS'],

  // 自治体組織リスト
  自治体: [
    '春日部市役所',
    '川口市役所',
    'さいたま市役所',
    '越谷市役所',
    '草加市役所',
    '八潮市役所',
    '三郷市役所',
    '吉川市役所'
  ],

  // 収集企業リスト
  清掃業者: [
    '春日部清掃株式会社',
    '埼玉環境サービス株式会社',
    '東日本清掃工業株式会社',
    'クリーン産業株式会社',
    '関東廃棄物処理株式会社',
    '首都圏清掃サービス株式会社'
  ]
}

// モックデータ - アカウント情報
let mockAccounts = [
  {
    id: 1,
    userId: 'admin',
    attribute: '開発者',
    company: 'PITS',
    password: 'password123',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 2,
    userId: 'user001',
    attribute: '自治体',
    company: '春日部市役所',
    password: 'user123',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 3,
    userId: 'guest',
    attribute: '清掃業者',
    company: '春日部清掃株式会社',
    password: 'guest123',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
]

// 設定
const MOCK_CONFIG = {
  // ネットワーク遅延（ミリ秒）
  delays: {
    get: 300,
    post: 500,
    put: 400,
    delete: 300
  },
  // エラー率（0-1の範囲、0.1 = 10%の確率でエラー）
  errorRate: 0,
  // ログ出力の有効/無効
  enableLogging: true
}

/**
 * 遅延をシミュレートするヘルパー関数
 * @param {number} ms - 遅延時間（ミリ秒）
 * @returns {Promise<void>}
 */
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * ランダムエラーをシミュレートするヘルパー関数
 * @param {string} operation - 操作名
 * @throws {Error} 設定されたエラー率に基づいてランダムにエラーを発生
 */
const simulateRandomError = (operation) => {
  if (Math.random() < MOCK_CONFIG.errorRate) {
    throw new Error(`[MOCK ERROR] ${operation} 操作でネットワークエラーが発生しました`)
  }
}

/**
 * ログ出力ヘルパー関数
 * @param {string} level - ログレベル（info, warn, error）
 * @param {string} message - メッセージ
 * @param {any} data - 追加データ
 */
const log = (level, message, data = null) => {
  if (!MOCK_CONFIG.enableLogging) return

  const timestamp = new Date().toISOString()
  const logMessage = `[MOCK ${level.toUpperCase()}] ${timestamp}: ${message}`

  if (data) {
    console[level](logMessage, data)
  } else {
    console[level](logMessage)
  }
}

/**
 * 座標を小数点以下6桁に丸める
 * @param {number|null} coordinate - 座標値
 * @returns {number|null} 丸められた座標値
 */
const roundCoordinate = (coordinate) => {
  if (coordinate == null) return null
  return Math.round(coordinate * 1000000) / 1000000
}

// モックサービスのメイン実装
const developerSettingsService = {
  /**
   * 施設情報を取得
   * @returns {Promise<Array>} 施設情報の配列
   * @throws {Error} ネットワークエラーまたはサーバーエラー
   */
  async getFacilities() {
    log('info', '施設情報の取得を開始')

    try {
      await delay(MOCK_CONFIG.delays.get)
      simulateRandomError('施設情報取得')

      // ディープコピーで元データを保護
      const facilities = JSON.parse(JSON.stringify(mockFacilities))

      log('info', '施設情報の取得に成功', { count: facilities.length })
      return facilities

    } catch (error) {
      log('error', '施設情報の取得に失敗', error.message)
      throw error
    }
  },

  /**
   * 座標変換補正値を取得
   * @returns {Promise<Object>} 座標変換補正値
   * @throws {Error} ネットワークエラーまたはサーバーエラー
   */
  async getCoordinateCorrection() {
    log('info', '座標変換補正値の取得を開始')

    try {
      await delay(MOCK_CONFIG.delays.get)
      simulateRandomError('座標変換補正値取得')

      const correction = JSON.parse(JSON.stringify(mockCoordinateCorrection))

      log('info', '座標変換補正値の取得に成功', correction)
      return correction

    } catch (error) {
      log('error', '座標変換補正値の取得に失敗', error.message)
      throw error
    }
  },

  /**
   * 施設の位置情報を更新
   * @param {number} facilityId - 施設ID
   * @param {number|null} latitude - 緯度
   * @param {number|null} longitude - 経度
   * @returns {Promise<Object>} 更新された施設情報
   * @throws {Error} 施設が見つからない、またはネットワークエラー
   */
  async updateFacilityLocation(facilityId, latitude, longitude) {
    log('info', `施設位置情報の更新を開始`, { facilityId, latitude, longitude })

    try {
      await delay(MOCK_CONFIG.delays.put)
      simulateRandomError('施設位置情報更新')

      const facility = mockFacilities.find(f => f.id === facilityId)
      if (!facility) {
        throw new Error(`施設ID ${facilityId} が見つかりません`)
      }

      // 座標を6桁に丸めて保存
      facility.latitude = roundCoordinate(latitude)
      facility.longitude = roundCoordinate(longitude)
      facility.updatedAt = new Date().toISOString()

      const updatedFacility = JSON.parse(JSON.stringify(facility))

      log('info', `施設 "${facility.name}" の位置情報を更新しました`, updatedFacility)
      return updatedFacility

    } catch (error) {
      log('error', '施設位置情報の更新に失敗', error.message)
      throw error
    }
  },

  /**
   * 施設の位置情報をクリア（null に設定）
   * @param {number} facilityId - 施設ID
   * @returns {Promise<Object>} 更新された施設情報
   * @throws {Error} 施設が見つからない、またはネットワークエラー
   */
  async clearFacilityLocation(facilityId) {
    log('info', `施設位置情報のクリアを開始`, { facilityId })

    try {
      await delay(MOCK_CONFIG.delays.put)
      simulateRandomError('施設位置情報クリア')

      const facility = mockFacilities.find(f => f.id === facilityId)
      if (!facility) {
        throw new Error(`施設ID ${facilityId} が見つかりません`)
      }

      facility.latitude = null
      facility.longitude = null
      facility.updatedAt = new Date().toISOString()

      const updatedFacility = JSON.parse(JSON.stringify(facility))

      log('info', `施設 "${facility.name}" の位置情報をクリアしました`, updatedFacility)
      return updatedFacility

    } catch (error) {
      log('error', '施設位置情報のクリアに失敗', error.message)
      throw error
    }
  },

  /**
   * 座標変換補正値を更新
   * @param {number} latitudeOffset - 緯度補正値
   * @param {number} longitudeOffset - 経度補正値
   * @returns {Promise<Object>} 更新された座標変換補正値
   * @throws {Error} ネットワークエラーまたはサーバーエラー
   */
  async updateCoordinateCorrection(latitudeOffset, longitudeOffset) {
    log('info', '座標変換補正値の更新を開始', { latitudeOffset, longitudeOffset })

    try {
      await delay(MOCK_CONFIG.delays.put)
      simulateRandomError('座標変換補正値更新')

      // 座標を6桁に丸めて保存
      mockCoordinateCorrection.latitudeOffset = roundCoordinate(latitudeOffset)
      mockCoordinateCorrection.longitudeOffset = roundCoordinate(longitudeOffset)
      mockCoordinateCorrection.updatedAt = new Date().toISOString()

      const updatedCorrection = JSON.parse(JSON.stringify(mockCoordinateCorrection))

      log('info', '座標変換補正値を更新しました', updatedCorrection)
      return updatedCorrection

    } catch (error) {
      log('error', '座標変換補正値の更新に失敗', error.message)
      throw error
    }
  },

  /**
   * モック設定を更新（テスト・デバッグ用）
   * @param {Object} config - 新しい設定
   * @returns {Object} 更新された設定
   */
  updateMockConfig(config) {
    Object.assign(MOCK_CONFIG, config)
    log('info', 'モック設定を更新しました', MOCK_CONFIG)
    return JSON.parse(JSON.stringify(MOCK_CONFIG))
  },

  // ===========================
  // アカウント関連のメソッド
  // ===========================

  /**
   * アカウント一覧を取得
   * @returns {Promise<Array>} アカウント一覧
   */
  async getAccounts() {
    try {
      log('info', 'アカウント一覧取得開始')
      simulateRandomError('アカウント一覧取得')

      await delay(MOCK_CONFIG.delays.get)

      const accounts = JSON.parse(JSON.stringify(mockAccounts))
      log('info', 'アカウント一覧取得完了', { count: accounts.length })

      return accounts
    } catch (error) {
      log('error', 'アカウント一覧の取得に失敗', error.message)
      throw error
    }
  },

  /**
   * 新規アカウントを作成
   * @param {Object} accountData - 作成するアカウントデータ
   * @returns {Promise<Object>} 作成されたアカウント
   */
  async createAccount(accountData) {
    try {
      log('info', '新規アカウント作成開始', accountData)
      simulateRandomError('アカウント作成')

      // バリデーション
      if (!accountData.userId || !accountData.attribute || !accountData.company) {
        throw new Error('必須項目が不足しています')
      }

      // 重複チェック
      if (mockAccounts.find(acc => acc.userId === accountData.userId)) {
        throw new Error('指定されたユーザーIDは既に存在します')
      }

      await delay(MOCK_CONFIG.delays.post)

      const newAccount = {
        id: Math.max(...mockAccounts.map(acc => acc.id)) + 1,
        userId: accountData.userId,
        attribute: accountData.attribute,
        company: accountData.company,
        password: accountData.password || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      mockAccounts.push(newAccount)
      log('info', '新規アカウント作成完了', newAccount)

      return JSON.parse(JSON.stringify(newAccount))
    } catch (error) {
      log('error', '新規アカウントの作成に失敗', error.message)
      throw error
    }
  },

  /**
   * アカウント情報を更新
   * @param {number} accountId - 更新するアカウントのID
   * @param {Object} accountData - 更新データ
   * @returns {Promise<Object>} 更新されたアカウント
   */
  async updateAccount(accountId, accountData) {
    try {
      log('info', 'アカウント更新開始', { accountId, accountData })
      simulateRandomError('アカウント更新')

      const index = mockAccounts.findIndex(acc => acc.id === accountId)
      if (index === -1) {
        throw new Error(`ID ${accountId} のアカウントが見つかりません`)
      }

      // ユーザーID重複チェック（自分以外）
      if (accountData.userId &&
        mockAccounts.find(acc => acc.userId === accountData.userId && acc.id !== accountId)) {
        throw new Error('指定されたユーザーIDは既に存在します')
      }

      await delay(MOCK_CONFIG.delays.put)

      const updatedAccount = {
        ...mockAccounts[index],
        ...accountData,
        updatedAt: new Date().toISOString()
      }

      mockAccounts[index] = updatedAccount
      log('info', 'アカウント更新完了', updatedAccount)

      return JSON.parse(JSON.stringify(updatedAccount))
    } catch (error) {
      log('error', 'アカウントの更新に失敗', error.message)
      throw error
    }
  },

  /**
   * アカウントを削除
   * @param {number} accountId - 削除するアカウントのID
   * @returns {Promise<Object>} 削除結果
   */
  async deleteAccount(accountId) {
    try {
      log('info', 'アカウント削除開始', { accountId })
      simulateRandomError('アカウント削除')

      const index = mockAccounts.findIndex(acc => acc.id === accountId)
      if (index === -1) {
        throw new Error(`ID ${accountId} のアカウントが見つかりません`)
      }

      await delay(MOCK_CONFIG.delays.delete)

      const deletedAccount = mockAccounts.splice(index, 1)[0]
      log('info', 'アカウント削除完了', deletedAccount)

      return {
        success: true,
        deletedAccount: JSON.parse(JSON.stringify(deletedAccount)),
        message: 'アカウントを削除しました'
      }
    } catch (error) {
      log('error', 'アカウントの削除に失敗', error.message)
      throw error
    }
  },

  /**
   * モックデータをリセット（テスト用）
   * @returns {void}
   */
  resetMockData() {
    mockFacilities = [
      {
        id: 1,
        name: '市役所',
        latitude: 35.975411,
        longitude: 139.757172,
        updatedAt: new Date().toISOString()
      },
      {
        id: 2,
        name: 'クリーンセンター①',
        latitude: null,
        longitude: null,
        updatedAt: new Date().toISOString()
      },
      {
        id: 3,
        name: 'クリーンセンター②',
        latitude: null,
        longitude: null,
        updatedAt: new Date().toISOString()
      }
    ]

    mockCoordinateCorrection = {
      latitudeOffset: 0.000001,
      longitudeOffset: -0.000001,
      updatedAt: new Date().toISOString()
    }

    mockAccounts = [
      {
        id: 1,
        userId: 'admin',
        attribute: '開発者',
        company: 'PITS',
        password: 'password123',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 2,
        userId: 'user001',
        attribute: '自治体',
        company: '春日部市役所',
        password: 'user123',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 3,
        userId: 'guest',
        attribute: '清掃業者',
        company: '春日部清掃株式会社',
        password: 'guest123',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ]

    log('info', 'モックデータをリセットしました')
  },

  /**
   * 属性に応じた企業リストを取得
   * @param {string} attribute - 属性（開発者、自治体、清掃業者）
   * @returns {Promise<Array<string>>} 企業リスト
   */
  async getCompanyList(attribute) {
    try {
      simulateRandomError('企業リスト取得')
      await delay(MOCK_CONFIG.delays.get)

      log('info', `企業リスト取得 - 属性: ${attribute}`)

      // 開発者の場合は固定値
      if (attribute === '開発者') {
        const companies = mockCompanyLists[attribute]
        log('info', `企業リスト取得完了 - ${companies.length}件`)
        return companies
      }

      // 自治体の場合は自治体組織サービスから取得
      if (attribute === '自治体') {
        try {
          // 動的にimport（循環参照を避けるため）
          const municipalOrganizationService = await import('../systemSettings/municipalOrganizationService.js')
          const response = await municipalOrganizationService.default.getMunicipalOrganizations()

          if (response.success && Array.isArray(response.data)) {
            const companies = response.data.map(org => org.organizationName)
            log('info', `自治体組織リスト取得完了 - ${companies.length}件`, companies)
            return companies
          } else {
            log('warn', '自治体組織データの取得に失敗、モックデータを使用')
            return mockCompanyLists[attribute] || []
          }
        } catch (error) {
          log('error', '自治体組織サービスからの取得エラー:', error.message)
          log('info', 'モックデータを使用')
          return mockCompanyLists[attribute] || []
        }
      }

      // 清掃業者の場合は収集企業サービスから取得
      if (attribute === '清掃業者') {
        try {
          // 動的にimport（循環参照を避けるため）
          const collectionCompanyService = await import('../systemSettings/collectionCompanyService.js')
          const response = await collectionCompanyService.default.getCollectionCompanies()

          if (response.success && Array.isArray(response.data)) {
            const companies = response.data.map(company => company.companyName)
            log('info', `収集企業リスト取得完了 - ${companies.length}件`, companies)
            return companies
          } else {
            log('warn', '収集企業データの取得に失敗、モックデータを使用')
            return mockCompanyLists[attribute] || []
          }
        } catch (error) {
          log('error', '収集企業サービスからの取得エラー:', error.message)
          log('info', 'モックデータを使用')
          return mockCompanyLists[attribute] || []
        }
      }

      // その他の属性は無効
      const error = new Error(`無効な属性です: ${attribute}`)
      log('error', error.message)
      throw error

    } catch (error) {
      log('error', '企業リスト取得エラー:', error.message)
      throw error
    }
  }
}

export default developerSettingsService
